/**
 * Aspect — PBR texture sets (Poly Haven, CC0) + finish registry
 * --------------------------------------------------------------
 * Real photographic PBR maps (diffuse / normal / roughness, 1K JPG) that
 * asynchronously upgrade the procedural canvas materials from
 * surface_library.js. The procedural maps stay as the instant zero-download
 * fallback; when a set finishes loading we swap the maps on the SAME shared
 * material instance (engine rule: one material per surface type per build —
 * upgrading in place keeps every wall/floor segment and the cutaway system
 * pointing at the same material).
 *
 * The FINISHES registry is also the catalog for user-selectable surface
 * finishes (walls: plaster/concrete/paint tints + feature walls; floors:
 * laminate, herringbone, dark wood, tile, marble). `applyFinish` re-targets a
 * shared material at a different set, which is how per-surface finish switching works.
 *
 * Textures are cached per-app so repeated room rebuilds reuse GPU resources.
 */

import * as pc from "playcanvas";
import { mobileGraphicsTier } from "../device_profile.js";

const TEX_BASE = "./assets/textures";

/**
 * Finish registry. `size` is the physical meters one texture repeat covers
 * (used for meter-based floor tiling). `tint` multiplies the diffuse map.
 * Wall finishes with `paintOnly: true` reuse the plaster maps with a color.
 */
export const FINISHES = {
  // --- Walls (HouseProps HDRP pack) ---
  wall_hp_default: {
    label: "HP Plaster White",
    role: "wall",
    set: "hp_wall_default",
    size: 2.6,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps plaster",
  },
  wall_hp_concrete: {
    label: "HP Concrete",
    role: "wall",
    set: "hp_wall_concrete",
    size: 2.6,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps concrete",
  },
  wall_hp_blue: {
    label: "HP Blue",
    role: "wall",
    set: "hp_wall_blue",
    size: 2.6,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps paint · blue",
  },
  wall_hp_brown: {
    label: "HP Brown",
    role: "wall",
    set: "hp_wall_brown",
    size: 2.6,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps paint · brown",
  },
  wall_hp_green: {
    label: "HP Green",
    role: "wall",
    set: "hp_wall_green",
    size: 2.6,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps paint · green",
  },

  // --- Floors (HouseProps HDRP pack) ---
  floor_hp_hardwood: {
    label: "HP Hardwood",
    role: "floor",
    set: "hp_floor_hardwood",
    size: 2.0,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps hardwood",
  },
  floor_hp_concrete: {
    label: "HP Concrete",
    role: "floor",
    set: "hp_floor_concrete",
    size: 2.2,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps concrete",
  },

  // --- Ceiling (HouseProps HDRP pack) ---
  ceiling_hp_default: {
    label: "HP Ceiling",
    role: "ceiling",
    set: "hp_ceiling_default",
    size: 3.0,
    tint: [1.0, 1.0, 1.0],
    material: "HouseProps ceiling",
  },

  // --- Walls ---
  wall_plaster: {
    label: "Painted Plaster",
    role: "wall",
    set: "painted_plaster_wall",
    size: 2.5,
    tint: [0.98, 0.97, 0.95],
    material: "Painted plaster",
  },
  wall_concrete: {
    label: "Concrete",
    role: "wall",
    set: "concrete_wall_008",
    size: 3.0,
    tint: [1.0, 1.0, 1.0],
    material: "Cast concrete",
  },
  wall_paint_white: {
    label: "White Paint",
    role: "wall",
    set: "painted_plaster_wall",
    size: 2.5,
    tint: [1.0, 1.0, 1.0],
    material: "Matte paint · white",
  },
  wall_paint_sage: {
    label: "Sage Green",
    role: "wall",
    set: "painted_plaster_wall",
    size: 2.5,
    tint: [0.72, 0.8, 0.68],
    material: "Matte paint · sage",
  },
  wall_paint_sand: {
    label: "Warm Sand",
    role: "wall",
    set: "painted_plaster_wall",
    size: 2.5,
    tint: [0.92, 0.84, 0.72],
    material: "Matte paint · sand",
  },
  wall_paint_slate: {
    label: "Slate Blue",
    role: "wall",
    set: "painted_plaster_wall",
    size: 2.5,
    tint: [0.62, 0.68, 0.78],
    material: "Matte paint · slate",
  },
  wall_wood_panels: {
    label: "Walnut Paneling",
    role: "wall",
    set: "wooden_panels",
    size: 2.1,
    tint: [1.0, 1.0, 1.0],
    material: "Walnut raised panels",
  },
  wall_exposed_brick: {
    label: "Exposed Brick",
    role: "wall",
    set: "brick_wall_001",
    size: 2.0,
    tint: [1.0, 1.0, 1.0],
    material: "Red brick",
  },
  wall_river_stone: {
    label: "River Stone",
    role: "wall",
    set: "stacked_stone_wall",
    size: 2.0,
    tint: [1.0, 1.0, 1.0],
    material: "Stacked river stone",
  },

  // --- Floors ---
  floor_laminate: {
    label: "Oak Laminate",
    role: "floor",
    set: "laminate_floor_02",
    size: 2.0,
    tint: [1.0, 0.98, 0.95],
    material: "Oak laminate",
  },
  floor_herringbone: {
    label: "Herringbone Parquet",
    role: "floor",
    set: "herringbone_parquet",
    size: 1.8,
    tint: [1.0, 0.97, 0.92],
    material: "Herringbone parquet",
  },
  floor_dark_wood: {
    label: "Dark Wood Planks",
    role: "floor",
    set: "dark_wooden_planks",
    size: 2.2,
    tint: [1.0, 1.0, 1.0],
    material: "Stained oak planks",
  },
  floor_tile_large: {
    label: "Porcelain Tile",
    role: "floor",
    set: "large_floor_tiles_02",
    size: 1.6,
    tint: [1.0, 1.0, 1.0],
    material: "Porcelain tile",
  },
  floor_marble: {
    label: "Marble",
    role: "floor",
    set: "marble_01",
    size: 2.0,
    tint: [1.0, 1.0, 1.0],
    material: "Polished marble",
  },

  // --- Ground ---
  grass_leafy: {
    label: "Lawn",
    role: "grass",
    set: "leafy_grass",
    size: 1.5,
    tint: [1.0, 1.0, 1.0],
    material: "Lawn",
  },
};

export const DEFAULT_WALL_FINISH = "wall_hp_default";
export const DEFAULT_FLOOR_FINISH = "floor_hp_hardwood";
export const DEFAULT_WET_FLOOR_FINISH = "floor_marble";
export const DEFAULT_CEILING_FINISH = "ceiling_hp_default";

/** Finishes the user may pick for a given role, in display order. */
export function finishOptions(role) {
  return Object.entries(FINISHES)
    .filter(([, f]) => f.role === role)
    .map(([id, f]) => ({ id, label: f.label }));
}

// --- Per-app texture cache ----------------------------------------------------

/** @type {WeakMap<pc.AppBase, Map<string, Promise<pc.Texture>>>} */
const caches = new WeakMap();

function cacheFor(app) {
  let c = caches.get(app);
  if (!c) {
    c = new Map();
    caches.set(app, c);
  }
  return c;
}

function loadTexture(app, url, { srgb = true } = {}) {
  const cache = cacheFor(app);
  const key = `${url}|${srgb ? 1 : 0}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const p = new Promise((resolve, reject) => {
    const asset = new pc.Asset(`aspect-tex-${key}`, "texture", { url }, {
      srgb,
      addressu: "repeat",
      addressv: "repeat",
      anisotropy: 8,
      mipmaps: true,
    });
    asset.once("load", () => resolve(asset.resource));
    asset.once("error", (err) => {
      cache.delete(key);
      reject(err ?? new Error(`texture load failed: ${url}`));
    });
    app.assets.add(asset);
    app.assets.load(asset);
  });
  cache.set(key, p);
  return p;
}

/** Load the diffuse/normal/roughness triplet of a Poly Haven set. */
function loadSet(app, set) {
  const dir = `${TEX_BASE}/${set}`;
  return Promise.all([
    loadTexture(app, `${dir}/${set}_diff_1k.jpg`, { srgb: true }),
    loadTexture(app, `${dir}/${set}_nor_gl_1k.jpg`, { srgb: false }),
    loadTexture(app, `${dir}/${set}_rough_1k.jpg`, { srgb: false }),
  ]);
}

/**
 * Asynchronously re-target a shared StandardMaterial at a finish's PBR maps.
 * Never creates a new material — swaps maps in place once loaded, so every
 * mesh instance (and the cutaway fade cache) keeps the same reference.
 *
 * @param {pc.AppBase} app
 * @param {pc.StandardMaterial} material shared surface material to upgrade
 * @param {string} finishId key into FINISHES
 * @param {{ tileW?: number, tileD?: number, tiling?: pc.Vec2 }} [opts]
 *   tileW/tileD: physical meters covered (floors) → meter-based repeat counts.
 *   tiling: explicit repeat counts (walls keep their existing UV convention).
 * @returns {Promise<boolean>} resolves true when the maps are applied
 */
export async function applyFinish(app, material, finishId, opts = {}) {
  // Crash-loop phones skip 1K PBR. Bedroom HQ can hold wall/floor/ceiling maps.
  if (mobileGraphicsTier() === "safe") return false;
  const finish = FINISHES[finishId];
  if (!finish || !material) return false;
  let maps;
  try {
    maps = await loadSet(app, finish.set);
  } catch (err) {
    console.warn(`[aspect] finish "${finishId}" textures failed; keeping procedural`, err);
    return false;
  }
  // The app may have been torn down (HMR) while maps were loading.
  if (!app.graphicsDevice || app._destroyRequested) return false;

  const [diff, nor, rough] = maps;
  const tiling =
    opts.tiling ??
    (opts.tileW != null
      ? new pc.Vec2(
          Math.max(1, (opts.tileW ?? finish.size) / finish.size),
          Math.max(1, (opts.tileD ?? opts.tileW ?? finish.size) / finish.size),
        )
      : material.diffuseMapTiling?.clone() ?? new pc.Vec2(1, 1));

  material.diffuse = new pc.Color(finish.tint[0], finish.tint[1], finish.tint[2]);
  material.diffuseMap = diff;
  material.diffuseMapTiling = tiling;
  material.normalMap = nor;
  material.normalMapTiling = tiling;
  material.bumpiness = finish.role === "grass" ? 0.9 : 0.6;
  // Poly Haven ships roughness; StandardMaterial wants gloss — invert in shader.
  material.glossMap = rough;
  material.glossMapTiling = tiling;
  material.glossInvert = true;
  material.gloss = 1.0;
  material.update();
  app.renderNextFrame = true;
  return true;
}

/** Standard wall-material tiling for a given wall height (shared convention). */
export function wallTiling(height = 2.8) {
  return new pc.Vec2(2, Math.max(1, height / 1.4));
}

/** Human-readable finish name for spec cards; null for unknown ids. */
export function finishLabel(finishId) {
  return FINISHES[finishId]?.label ?? null;
}

export default FINISHES;
